
import { defineComponent, ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import StarXpGate from '@/components/star/StarXpGate.vue'
import { isXpLowVersion } from '@/utils/xpEnv'
import StarTabBar from '@/components/star/StarTabBar.vue'
import FactorSelector from '@/components/star/FactorSelector.vue'
import StockRangeSelector from '@/components/star/StockRangeSelector.vue'
import TradingRules from '@/components/star/TradingRules.vue'
import PositionControl from '@/components/star/PositionControl.vue'
import BacktestPeriod from '@/components/star/BacktestPeriod.vue'
import StarTipDialog from '@/components/star/StarTipDialog.vue'
import StarActivationDialog from '@/components/star/StarActivationDialog.vue'
import { useStarTheme, useStarRouter } from '@/utils/useStarTheme'
import starApi from '@/api/starApi'
import { useStarTip } from '@/utils/useStarTip'
import { useStarActivation } from '@/utils/useStarActivation'
import { normalizeSelectedOptionsFromTree, resolveFactorSubLabelText } from '@/utils/starFactorLabel'
import {
  buildRangeOptionFromSelectedRange,
  buildRangeDescriptFromSelectedRange,
  parseWatchwordRangeBounds,
  selectedRangeFromBounds,
} from '@/utils/starRangeBounds'
import { parseWatchwordApiResponse } from '@/utils/starWatchwordHelper'
import getTooltipPlacements from 'ant-design-vue/es/tooltip/placements'

/** 略减小「向上」偏移，让气泡与触发文字更贴近；保留默认样式与箭头 */
function buildStrategyTooltipPlacements() {
  const raw = getTooltipPlacements({ arrowPointAtCenter: false, autoAdjustOverflow: true })
  const out: Record<string, any> = {}
  for (const key of Object.keys(raw)) {
    const item = raw[key] as any
    out[key] = Object.assign({}, item, {
      offset: Array.isArray(item.offset) ? item.offset.slice() : item.offset,
      overflow: item.overflow ? Object.assign({}, item.overflow) : item.overflow,
    })
  }
  const dy = -1
  for (const key of ['top', 'topLeft', 'topRight']) {
    if (out[key]?.offset?.length >= 2) {
      out[key].offset = [out[key].offset[0], dy]
    }
  }
  return out
}

const strategyBuiltinPlacements = buildStrategyTooltipPlacements()

const BOARD_RANGE_KEYWORDS = ['行业', '概念', '题材', '地区']

function isBoardRangeItem(name: string) {
  return BOARD_RANGE_KEYWORDS.some((kw) => String(name || '').includes(kw))
}

/** getFactors 可能返回数组或包一层 list，统一成 tab 数组 */
function resolveFactorTree(raw: any): any[] {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  const tree = raw.list ?? raw.factors ?? raw.factorList ?? raw.childAttrList ?? raw.userFactors
  return Array.isArray(tree) ? tree : []
}

function findLeafInFactorTabs(tabs: any[], code: string): any | null {
  const walk = (nodes: any[]): any | null => {
    for (const n of nodes || []) {
      if (n.attrType != null && n.attrType !== undefined && String(n.attrCode) === String(code)) {
        return n
      }
      const child = walk(n.childAttrList || [])
      if (child) return child
    }
    return null
  }
  for (const tab of resolveFactorTree(tabs)) {
    const found = walk(tab.childAttrList || [])
    if (found) return found
  }
  return null
}

function findLeafInFactorTabsByName(tabs: any[], name: string): any | null {
  const target = String(name || '').trim()
  if (!target) return null
  const walk = (nodes: any[]): any | null => {
    for (const n of nodes || []) {
      if (n.attrType != null && n.attrType !== undefined && String(n.attrName || '').trim() === target) {
        return n
      }
      const child = walk(n.childAttrList || [])
      if (child) return child
    }
    return null
  }
  for (const tab of resolveFactorTree(tabs)) {
    const found = walk(tab.childAttrList || [])
    if (found) return found
  }
  return null
}

/** 按名称关键字查找叶子（如「剔除ST及退市整理」） */
function findLeafByAttrNameKeyword(tabs: any[], keyword: string): any | null {
  const kw = String(keyword || '').trim()
  if (!kw) return null
  const walk = (nodes: any[]): any | null => {
    for (const n of nodes || []) {
      if (n.attrType != null && n.attrType !== undefined && String(n.attrName || '').includes(kw)) {
        return n
      }
      const child = walk(n.childAttrList || [])
      if (child) return child
    }
    return null
  }
  for (const tab of tabs || []) {
    const found = walk(tab.childAttrList || [])
    if (found) return found
  }
  return null
}

/** 解析选股范围 factors 接口：树 + 已选列表（非口令时可能下发） */
function parseRangeFactorsApiData(data: any): { tree: any[] | null; selected: any[] } {
  if (!data) return { tree: null, selected: [] }
  if (Array.isArray(data)) return { tree: data, selected: [] }
  const tree =
    data.list ?? data.factors ?? data.factorList ?? data.childAttrList ?? data.userFactors ?? null
  const selected =
    data.selected ?? data.stockRange ?? data.selectedList ?? data.userSelected ?? data.selectedStockRange ?? []
  return {
    tree: Array.isArray(tree) ? tree : null,
    selected: Array.isArray(selected) ? selected : [],
  }
}

const resolveRangeTree = resolveFactorTree

function mapPlateOptionsFromApi(raw: any[]): any[] {
  return raw.map((o: any) => ({
    attrCode: String(o.plateCode ?? o.optionCode ?? o.attrCode ?? ''),
    attrName: String(o.plateName ?? o.optionName ?? o.attrName ?? ''),
    changeRate: o.pxChangeRate ?? o.changeRate,
    market: o.market,
    firstLetter: o.firstLetter ?? '',
  })).filter((o) => o.attrCode)
}

/** 口令板块：options 或 descript（如「上海板块」）匹配子项 */
function resolveBoardSelectedOptions(plateList: any[], apiItem: any): any[] {
  const opts = apiItem.options || []
  const descript = apiItem.descript != null ? String(apiItem.descript).trim() : ''

  if (opts.length) {
    const byCode = new Map(plateList.map((o) => [String(o.attrCode), o]))
    const byName = new Map(plateList.map((o) => [String(o.attrName).trim(), o]))
    return opts.map((raw: any) => {
      if (raw && typeof raw === 'object' && !('gte' in raw)) {
        const code = String(raw.optionCode ?? raw.attrCode ?? raw.plateCode ?? '')
        const name = raw.descript ?? raw.optionName ?? raw.attrName ?? raw.plateName
        if (code && byCode.has(code)) return byCode.get(code)!
        if (name && byName.has(String(name).trim())) return byName.get(String(name).trim())!
        if (name) return { attrCode: code || String(name), attrName: String(name) }
        return { attrCode: code, attrName: code }
      }
      const code = String(raw)
      return byCode.get(code) || { attrCode: code, attrName: code }
    }).filter((o) => o?.attrCode)
  }

  if (!descript) return []

  const parts = descript.split(/[、,，;；]/).map((s) => s.trim()).filter(Boolean)
  const targets = parts.length ? parts : [descript]
  const matched: any[] = []
  for (const t of targets) {
    const hit = plateList.find(
      (p) =>
        p.attrName === t ||
        t.includes(p.attrName) ||
        p.attrName.includes(t),
    )
    if (hit && !matched.some((m) => m.attrCode === hit.attrCode)) matched.push(hit)
  }
  if (matched.length) return matched
  return [{ attrCode: descript, attrName: descript }]
}

function finalizeWatchwordRangeItem(item: any, apiItem: any) {
  const descript = apiItem.descript != null ? String(apiItem.descript).trim() : ''
  const out = finalizeWatchwordFactorDisplay(item, apiItem)
  if (out.attrType === 1 || out.attrType == null) {
    out._confirmed = true
  }
  if (descript && !(out.selectedOptions?.length) && out.attrType === 3) {
    out.selectedOptions = [{ attrCode: out.attrCode, attrName: descript }]
    out._confirmed = true
  }
  return out
}

function buildDefaultRemoveStSelection(rangeTreeRaw: any[]): any[] | null {
  const rangeTree = resolveRangeTree(rangeTreeRaw)
  const leaf =
    findLeafInFactorTabs(rangeTree, 'remove_st') ||
    findLeafByAttrNameKeyword(rangeTree, '剔除ST及退市整理') ||
    findLeafByAttrNameKeyword(rangeTree, '剔除ST')
  if (!leaf) return null
  const type = leaf.attrType
  if (type === 1 || type == null) {
    return [{ ...leaf, selectedOptions: [] }]
  }
  return [{ ...leaf, selectedOptions: [], _confirmed: true }]
}

function collectOptionNodes(leaf: any): any[] {
  const list: any[] = []
  const walk = (nodes: any[]) => {
    for (const n of nodes || []) {
      if (n.attrType != null && n.attrType !== undefined && String(n.attrCode) !== String(leaf.attrCode)) {
        list.push(n)
      }
      walk(n.childAttrList || [])
    }
  }
  walk(leaf.childAttrList || [])
  return list
}

function isCodeLikeDisplayName(name: string, code: string) {
  const n = String(name || '').trim()
  const c = String(code || '').trim()
  if (!n) return true
  if (c && n === c) return true
  return /^[a-z][a-z0-9_]*$/i.test(n)
}

function resolveOptionNodes(leaf: any, apiOptions: any[], apiItem?: any): any[] {
  if (!apiOptions?.length) return []
  const known = collectOptionNodes(leaf)
  const byCode = new Map(known.map((o) => [String(o.attrCode), o]))
  const factorDescript = apiItem?.descript != null ? String(apiItem.descript).trim() : ''
  const factorCode = String(apiItem?.factorCode ?? apiItem?.attrCode ?? leaf?.attrCode ?? '')
  /** 多选时不能把整条因子 descript 赋给每个子项，否则拼接会重复 */
  const useFactorDescriptAsName = apiOptions.length === 1

  const pickOptionName = (code: string, rawName: string | undefined, node: any) => {
    const fromNode = node?.attrName != null ? String(node.attrName).trim() : ''
    if (fromNode && !isCodeLikeDisplayName(fromNode, code)) return fromNode
    const fromRaw = rawName != null ? String(rawName).trim() : ''
    if (
      fromRaw &&
      !isCodeLikeDisplayName(fromRaw, code) &&
      (useFactorDescriptAsName || !/[或且]/.test(fromRaw)) &&
      (!factorDescript || fromRaw !== factorDescript)
    ) {
      return fromRaw
    }
    if (useFactorDescriptAsName && factorDescript) return factorDescript
    return fromNode || fromRaw || code
  }

  return apiOptions.map((raw) => {
    if (raw && typeof raw === 'object' && !('gte' in raw) && !('lte' in raw)) {
      const code = String(raw.optionCode ?? raw.attrCode ?? raw.plateCode ?? '')
      const name = raw.descript ?? raw.optionName ?? raw.attrName ?? raw.plateName
      if (code && byCode.has(code)) {
        const node = byCode.get(code)
        return { ...node, attrName: pickOptionName(code, name, node) }
      }
      return { attrCode: code, attrName: pickOptionName(code, name, undefined) }
    }
    const code = String(raw)
    const node = byCode.get(code)
    if (node) {
      return { ...node, attrName: pickOptionName(code, undefined, node) }
    }
    if (factorDescript && (code === factorCode || isCodeLikeDisplayName(code, code))) {
      return { attrCode: code, attrName: factorDescript }
    }
    if (factorDescript && !isCodeLikeDisplayName(code, code)) {
      return { attrCode: code, attrName: code }
    }
    return { attrCode: code, attrName: code }
  }).filter((o) => o?.attrCode)
}

function resolveWatchwordSelectedOptions(leaf: any, apiOptions: any[], apiItem?: any): any[] {
  return normalizeSelectedOptionsFromTree(
    resolveOptionNodes(leaf, apiOptions, apiItem),
    leaf?.childAttrList,
  )
}

function attachSelectionDescript(item: any, apiItem: any) {
  const text = apiItem.descript ?? apiItem.description
  if (text != null && String(text).trim() !== '') {
    item.selectionDescript = String(text).trim()
  }
  return item
}

/** 口令区间因子：从 options（含字符串 JSON）/ descript 解析已选区间 */
function parseWatchwordRange(apiItem: any, factorName = '') {
  return parseWatchwordRangeBounds(apiItem, factorName)
}

/** 口令回显：子项展示用接口 descript，避免左侧列表出现 factorCode / optionCode */
function finalizeWatchwordFactorDisplay(item: any, apiItem: any) {
  if (!item || !apiItem) return item
  const descript = apiItem.descript != null ? String(apiItem.descript).trim() : ''
  if (!descript) return attachSelectionDescript(item, apiItem)

  const opts: any[] = item.selectedOptions || []
  const optsAreCodes = opts.length > 0 && opts.every((o: any) => isCodeLikeDisplayName(o.attrName, o.attrCode))

  if (opts.length === 0 && (item.attrType === 1 || item.attrType == null)) {
    item.selectedOptions = [{ attrCode: item.attrCode, attrName: descript }]
    item._confirmed = true
  } else if (opts.length === 1 && optsAreCodes) {
    opts[0].attrName = descript
  } else if (opts.length > 1 && optsAreCodes) {
    item.selectionDescript = descript
  }

  return attachSelectionDescript(item, apiItem)
}

/** 口令回显：接口 factor / stockRange 项 → 选择器内部结构 */
function mapWatchwordApiItemToSelected(apiItem: any, tabs: any[], forRange = false): any | null {
  const code = apiItem.factorCode ?? apiItem.attrCode
  if (!code) return null
  const leaf =
    findLeafInFactorTabs(tabs, code) ||
    findLeafInFactorTabsByName(tabs, apiItem.factorName)
  const displayName = apiItem.descript ?? apiItem.factorName ?? leaf?.attrName ?? code
  if (!leaf) {
    const opts = apiItem.options || []
    return finalizeWatchwordFactorDisplay({
      attrCode: code,
      attrName: apiItem.factorName ?? displayName,
      attrType: opts.length ? 3 : 1,
      selectedOptions: resolveWatchwordSelectedOptions({ childAttrList: [] }, opts, apiItem),
      _confirmed: !!opts.length,
    }, apiItem)
  }
  const type = leaf.attrType
  const base = { ...leaf }
  const opts = apiItem.options || []
  if (type === 1) {
    return finalizeWatchwordRangeItem({ ...base, selectedOptions: [], _confirmed: true }, apiItem)
  }
  if (type === 2) {
    return finalizeWatchwordFactorDisplay({
      ...base,
      selectedOptions: resolveWatchwordSelectedOptions(leaf, opts, apiItem).slice(0, 1),
      _confirmed: true,
    }, apiItem)
  }
  if (type === 3 || type === 5) {
    const selectedOptions = resolveWatchwordSelectedOptions(leaf, opts, apiItem)
    if (forRange && isBoardRangeItem(leaf.attrName || displayName) && selectedOptions.length) {
      return finalizeWatchwordFactorDisplay({ ...base, attrType: 5, selectedOptions, _isBoard: true }, apiItem)
    }
    return finalizeWatchwordFactorDisplay({ ...base, selectedOptions, _confirmed: true }, apiItem)
  }
  if (type === 4 || type === 6) {
    return finalizeWatchwordFactorDisplay({
      ...base,
      selectedOptions: resolveWatchwordSelectedOptions(leaf, opts, apiItem),
      // intersectionAndUnion: 0=并集, 1=交集
      unionMode: apiItem.intersectionAndUnion === 0 ? 'union' : 'intersect',
      _confirmed: true,
    }, apiItem)
  }
  if (type === 7) {
    const parsed = parseWatchwordRange(apiItem, base.attrName || displayName)
    const selectedRange = parsed
      ? selectedRangeFromBounds(parsed)
      : { left: '', right: '', leftOp: 'gte', rightOp: 'lte', leftCompare: '<=', rightCompare: '<=' }
    return finalizeWatchwordFactorDisplay({
      ...base,
      selectedRange,
      selectedOptions: [],
      _confirmed: true,
    }, apiItem)
  }
  return finalizeWatchwordFactorDisplay({
    ...base,
    selectedOptions: resolveWatchwordSelectedOptions(leaf, opts, apiItem),
    _confirmed: !!opts.length,
  }, apiItem)
}

/** 因子级 descript：页面上代表已选含义的文案（子项名 / 区间 / 因子名） */
function resolveFactorItemDescript(f: any): string | null {
  const type = f.attrType
  if (type === 7) {
    const r = f.selectedRange
    if (r && (r.left !== '' || r.right !== '')) {
      return (
        buildRangeDescriptFromSelectedRange(r, {
          name: f.attrName || '',
          unit: f.attrUnit || f.unit || '',
        }) ||
        (f.selectionDescript != null ? String(f.selectionDescript).trim() : '') ||
        null
      )
    }
    const hint = f.selectionDescript != null ? String(f.selectionDescript).trim() : ''
    return hint || null
  }
  if (type === 2 || type === 3 || type === 4 || type === 5 || type === 6) {
    const sub = resolveFactorSubLabelText(f)
    if (sub) return sub
  }
  return (f.attrName || '').trim() || null
}

/** 将因子/选股范围项转为回测 stockRange 与 stock/select/result 共用的 selected 元素 */
function mapStarSelectionToApiItem(f: any) {
  const type = f.attrType
  let options: any[] = []
  let intersectionAndUnion = 0

  if (type === 1 || type == null) {
    options = []
  } else if (type === 2) {
    options = f.selectedOptions?.length ? [f.selectedOptions[0].attrCode] : []
  } else if (type === 3 || type === 5) {
    options = (f.selectedOptions || []).map((o: any) => o.attrCode)
  } else if (type === 4 || type === 6) {
    options = (f.selectedOptions || []).map((o: any) => o.attrCode)
    intersectionAndUnion = f.unionMode === 'union' ? 0 : 1
  } else if (type === 7) {
    const r = f.selectedRange
    if (r && (r.left !== '' || r.right !== '')) {
      options = [buildRangeOptionFromSelectedRange(r)]
    }
  }

  return {
    factorCode: f.attrCode || f.factorCode || '',
    descript: resolveFactorItemDescript(f),
    intersectionAndUnion,
    options,
  }
}

const __default__ = defineComponent({
  name: 'StarGroup',
  components: { StarXpGate, StarTabBar, FactorSelector, StockRangeSelector, TradingRules, PositionControl, BacktestPeriod, StarTipDialog, StarActivationDialog },
  setup() {
    const route = useRoute()
    const store = useStore()
    const { isLight, colors } = useStarTheme()
    const { starPush } = useStarRouter()

    const isPassword = computed(() => route.query.isPassword === 'true')
    /** 有 watchWord 即兑换并回显；isPassword 仅控制标题/回测带口令/结果页 isFactor */
    const resolveRouteWatchWord = () => {
      const raw = route.query.watchWord
      if (raw == null || raw === '') return ''
      const word = Array.isArray(raw) ? raw[0] : raw
      return String(word).trim()
    }
    const hasWatchWord = computed(() => !!resolveRouteWatchWord())

    const starUserName = computed(() => {
      const userInfo = store.state.userInfo
      return userInfo?.userName || (userInfo as any)?.username || (route.query.username as string) || (route.query.userName as string) || ''
    })

    const strategyNameText = computed(() => {
      const n = watchwordData.value?.strategyName
      return (n != null && String(n).trim() !== '') ? String(n).trim() : '—'
    })
    const strategyNoteText = computed(() => {
      const t = watchwordData.value?.strategyNote
      return (t != null && String(t).trim() !== '') ? String(t).trim() : '—'
    })

    const comOptions = ref<any>(null)
    const factorOptions = ref<any>(null)
    const stockRangeOptions = ref<any>(null)
    const selectedFactors = ref<any[]>([])
    const factorInitialSelection = ref<any[]>([])
    const rangeInitialSelection = ref<any[]>([])
    const watchwordData = ref<any>(null)  // 口令回显数据

    const factorHeaders = computed(() => ({
      token: route.query.token as string,
      appcode: 'com.rjhy.uranus',
      userName: starUserName.value
    }))

    /** 口令模式：因子树与无权限说明均由接口下发，前端不再筛选或拼接文案 */
    const factorOptionsForDisplay = computed(() => {
      const tree = resolveFactorTree(factorOptions.value)
      return tree.length ? tree : null
    })

    const stockRangeOptionsForDisplay = computed(() => {
      const tree = resolveRangeTree(stockRangeOptions.value)
      return tree.length ? tree : null
    })

    const factorPasswordNoAuthHint = computed(() => {
      if (!hasWatchWord.value || !watchwordData.value) return ''
      const t = watchwordData.value.noPermissionToast
      return typeof t === 'string' ? t.trim() : ''
    })

    const onFactorChange = (factors: any[]) => {
      selectedFactors.value = factors
    }

    const selectedRanges = ref<any[]>([])
    /** 非口令：getFactors(stockrange) 若附带已选范围，在此暂存 */
    const pendingRangeApiSelected = ref<any[]>([])
    const rangeStockTotal = ref(0)
    const rangeStockList = ref<any[]>([])
    /** 摘要栏：选股范围变更时用 count:true 拉 total */
    const rangeStockLoading = ref(false)
    /** 已选股票弹窗：点击打开时用 count:false 拉 stocks */
    const rangeDialogListLoading = ref(false)
    let rangeResultTimer: ReturnType<typeof setTimeout> | null = null

    const formatStockCode = (s: any) => {
      const sym = String(s?.symbol ?? '').trim()
      const m = String(s?.market ?? '').trim().toLowerCase()
      let code = sym
      if (m && sym && !/^(sh|sz|bj)/i.test(sym)) {
        code = `${m}${sym}`
      }
      const stripped = code.replace(/^(sh|sz|bj)/i, '')
      return stripped || '—'
    }
    const formatPrice = (v: any) => {
      const n = Number(v)
      return v != null && v !== '' && isFinite(n) ? n.toFixed(2) : '—'
    }
    /** 接口涨跌幅多为小数比率（如 0.0003），展示为百分比需 ×100 */
    const pctForDisplay = (v: any) => {
      const n = Number(v)
      if (!isFinite(n) || v === '' || v == null) return null
      return n * 100
    }
    const formatPct = (v: any) => {
      const d = pctForDisplay(v)
      if (d == null) return '—'
      return `${d >= 0 ? '+' : ''}${d.toFixed(2)}%`
    }
    const pxRateColor = (v: any) => {
      const d = pctForDisplay(v)
      const c = colors.value
      if (d == null) return c.textPrimary
      if (d > 0) return '#e8392a'
      if (d < 0) return '#00b578'
      return c.textMuted
    }

    /**
     * @param count 与接口一致：true 时通常只关心 total；false 时返回 stocks 列表
     */
    const fetchRangeSelectResult = async (count: boolean) => {
      const ranges = selectedRanges.value
      if (!ranges.length) {
        rangeStockTotal.value = 0
        rangeStockList.value = []
        rangeStockLoading.value = false
        rangeDialogListLoading.value = false
        return
      }
      if (count) {
        rangeStockLoading.value = true
      } else {
        rangeDialogListLoading.value = true
      }
      try {
        const token = route.query.token as string
        const userInfo = store.state.userInfo
        const userName = userInfo?.userName || (userInfo as any)?.username
        const selected = ranges.map(mapStarSelectionToApiItem)
        const res = await starApi.getStockSelectResult({
          data: {
            selected,
            notLimit: true,
            count,
          },
          headers: { token, appcode: 'com.rjhy.uranus', userName },
        })
        const d = res?.data
        rangeStockTotal.value = typeof d?.total === 'number' ? d.total : 0
        if (count) {
          rangeStockList.value = []
        } else {
          const raw = d?.stocks ?? d?.stock
          rangeStockList.value = Array.isArray(raw) ? raw : []
        }
      } catch (e) {
        rangeStockTotal.value = 0
        if (count) {
          rangeStockList.value = []
        } else {
          rangeStockList.value = []
        }
      } finally {
        if (count) rangeStockLoading.value = false
        else rangeDialogListLoading.value = false
      }
    }

    const onOpenSelectedStockDialog = () => {
      if (!selectedRanges.value.length) return
      showStockDialog.value = true
      void fetchRangeSelectResult(false)
    }

    const onRangeChange = (ranges: any[]) => {
      selectedRanges.value = ranges
      if (rangeResultTimer) clearTimeout(rangeResultTimer)
      rangeResultTimer = setTimeout(() => {
        rangeResultTimer = null
        fetchRangeSelectResult(true)
      }, 350)
    }

    const fetchComOptions = async () => {
      try {
        const token = route.query.token as string
        const res = await starApi.getComOptions({
          headers: { token }
        })
        comOptions.value = res?.data || null
      } catch (e) { /* 静默失败 */ }
    }

    const fetchFactors = async () => {
      try {
        const token = route.query.token as string
        const userName = starUserName.value
        const headers = { token, appcode: 'com.rjhy.uranus', userName }
        const [factorRes, rangeRes] = await Promise.all([
          starApi.getFactors({ data: { from: 'quantstarfactor' }, headers }),
          starApi.getFactors({ data: { from: 'quantstarstockrange' }, headers })
        ])
        factorOptions.value = factorRes?.data || null
        const rangePayload = parseRangeFactorsApiData(rangeRes?.data)
        stockRangeOptions.value = rangePayload.tree
        pendingRangeApiSelected.value = rangePayload.selected
      } catch (e) { /* 静默失败，不阻断口令兑换 */ }
    }

    /** 将接口下发的 stockRange 列表映射为选择器已选项（与口令回显共用逻辑） */
    const mapRangeApiItemsToSelections = async (rangeApi: any[]) => {
      const rangeTree = resolveRangeTree(stockRangeOptions.value)
      if (!rangeTree.length || !rangeApi?.length) return [] as any[]

      const headers = factorHeaders.value
      const ranges: any[] = []
      for (const item of rangeApi) {
        const code = item.factorCode ?? item.attrCode
        if (!code) continue
        const leaf =
          findLeafInFactorTabs(rangeTree, code) ||
          findLeafInFactorTabsByName(rangeTree, item.factorName)
        const descript = item.descript != null ? String(item.descript).trim() : ''
        const isBoard =
          leaf &&
          isBoardRangeItem(leaf.attrName || item.factorName) &&
          ((item.options || []).length > 0 || !!descript)

        if (isBoard) {
          try {
            const res = await starApi.getStockFactorOptions({
              data: { factorCode: code },
              headers,
            })
            const plateList = mapPlateOptionsFromApi(Array.isArray(res?.data) ? res.data : [])
            const selectedOptions = resolveBoardSelectedOptions(plateList, item)
            ranges.push(
              finalizeWatchwordRangeItem(
                {
                  ...leaf,
                  attrCode: code,
                  attrType: 5,
                  selectedOptions,
                  _isBoard: true,
                  _confirmed: selectedOptions.length > 0,
                },
                item,
              ),
            )
          } catch {
            const mapped = mapWatchwordApiItemToSelected(item, rangeTree, true)
            if (mapped) ranges.push(finalizeWatchwordRangeItem(mapped, item))
          }
          continue
        }

        const mapped = mapWatchwordApiItemToSelected(item, rangeTree, true)
        if (mapped) ranges.push(finalizeWatchwordRangeItem(mapped, item))
      }
      return ranges
    }

    const applyStockRangeSelections = (ranges: any[]) => {
      if (!ranges.length) return
      rangeInitialSelection.value = ranges
      selectedRanges.value = ranges
      void fetchRangeSelectResult(true)
    }

    /** 有已选则回显；为空则默认「剔除ST及退市整理」（非口令或口令未下发范围时） */
    const applyStockRangeWithDefaultFallback = (ranges: any[], skipDefaultIfWatchwordEmpty = false) => {
      const rangeTree = resolveRangeTree(stockRangeOptions.value)
      if (!rangeTree.length) return
      if (ranges.length) {
        applyStockRangeSelections(ranges)
        return
      }
      if (skipDefaultIfWatchwordEmpty && hasWatchWord.value) return
      const defaults = buildDefaultRemoveStSelection(rangeTree)
      if (defaults?.length) {
        applyStockRangeSelections(defaults)
      }
    }

    /** 非口令：优先回显接口已选；否则默认「剔除ST及退市整理」 */
    const initStockRangeForNormalMode = async () => {
      if (hasWatchWord.value || !stockRangeOptions.value) return

      if (pendingRangeApiSelected.value.length) {
        const ranges = await mapRangeApiItemsToSelections(pendingRangeApiSelected.value)
        applyStockRangeWithDefaultFallback(ranges)
        return
      }

      applyStockRangeWithDefaultFallback([])
    }

    const fetchWatchword = async () => {
      const word = resolveRouteWatchWord()
      if (!word) return
      try {
        const token = route.query.token as string
        const userName = starUserName.value
        const res = await starApi.redeemWatchword({
          data: { watchWord: word },
          headers: { token, userName }
        })
        const payload = parseWatchwordApiResponse(res)
        if (payload) {
          watchwordData.value = payload
        }
      } catch (e) { /* 静默失败 */ }
    }

    const applyWatchwordFromRoute = async () => {
      await fetchWatchword()
      if (watchwordData.value) {
        await hydrateWatchwordSelections()
      }
    }

    /** 口令模式：将 watchword 接口的 factor / stockRange 回显到选择器 */
    const hydrateWatchwordSelections = async () => {
      const wd = watchwordData.value
      if (!wd) return

      const factorTree = resolveFactorTree(factorOptions.value)
      const rangeTree = stockRangeOptions.value

      if (factorTree.length) {
        const factorApi = wd.factor || wd.factors || []
        const factors = (Array.isArray(factorApi) ? factorApi : [])
          .map((item: any) => mapWatchwordApiItemToSelected(item, factorTree))
          .filter(Boolean) as any[]
        factorInitialSelection.value = factors
        selectedFactors.value = factors
      }

      const rangeApi = wd.stockRange || wd.stockRanges || []
      const rangeList = Array.isArray(rangeApi) ? rangeApi : []
      if (resolveRangeTree(stockRangeOptions.value).length) {
        const ranges = rangeList.length ? await mapRangeApiItemsToSelections(rangeList) : []
        applyStockRangeWithDefaultFallback(ranges, rangeList.length > 0)
      }
    }

    const goHome = () => starPush('/star')
    // 摘要数据
    const tradingData = ref<any>({ buyPriorityLabel: '', holdDaysLabel: '', indicatorsLabel: '', valid: false })
    const positionData = ref<any>({ initCapital: '100', maxSinglePos: '10', maxHoldCount: '10', maxDailyBuy: '2', valid: true })
    const backtestData = ref<any>({ periodLabel: '近1年', period: 'year1', valid: true })

    /** 第五步回测周期：非口令默认近1年；口令有 period 时用接口值 */
    const backtestPeriodInitial = computed(() => {
      const wd = watchwordData.value
      if (wd?.period) {
        return {
          period: wd.period,
          periodStart: wd.periodStart ?? null,
          periodEnd: wd.periodEnd ?? null,
        }
      }
      return { period: 'year1' }
    })
    const showStockDialog = ref(false)
    const backtesting = ref(false)
    const showFactorTip = ref(false)
    const showToast = ref(false)
    const toastMsg = ref('')
    const {
      tipDialog, unlock, handleOneClickBacktestStatus,
      handleUnlockClose, handleUnlockBannerClick, handleUnlockShareClose, handleUnlockPromoClose,
      handleUnlockPurchaseClick,
    } = useStarTip()
    const {
      showActivationDialog,
      showActivationQr,
      openActivationQr,
      refreshActivation,
      hideActivation,
    } = useStarActivation()

    const showToastMsg = (msg: string) => {
      toastMsg.value = msg
      showToast.value = true
      setTimeout(() => { showToast.value = false }, 2000)
    }

    const onTradingChange = (data: any) => { tradingData.value = data }
    const onPositionChange = (data: any) => { positionData.value = data }
    const onBacktestChange = (data: any) => { backtestData.value = data }

    const sellSummary = computed(() => {
      const parts: string[] = []
      if (tradingData.value.holdDaysLabel) parts.push(`持股周期${tradingData.value.holdDaysLabel}卖出`)
      if (tradingData.value.indicatorsLabel) parts.push(tradingData.value.indicatorsLabel)
      const stops: string[] = []
      if (tradingData.value.stopProfit) stops.push(`止盈收益率≥${tradingData.value.stopProfit}%`)
      if (tradingData.value.stopLoss) stops.push(`止损收益率-${tradingData.value.stopLoss}%`)
      if (tradingData.value.stockLoss) stops.push(`个股收益≥${tradingData.value.stockLoss}%`)
      if (tradingData.value.maxDrawdown) stops.push(`最高收益回落≥${tradingData.value.maxDrawdown}%`)
      if (stops.length) parts.push(stops.join('，'))
      if (tradingData.value.sellTimeLabel) parts.push(tradingData.value.sellTimeLabel)
      return parts.join('；')
    })

    const positionSummary = computed(() => {
      const d = positionData.value
      const parts: string[] = []
      if (d.initCapital) parts.push(`初始资金${d.initCapital}万`)
      if (d.maxSinglePos) parts.push(`交易${d.maxSinglePos}%`)
      if (d.maxHoldCount) parts.push(`账户最大持股数${d.maxHoldCount}只`)
      if (d.maxDailyBuy) parts.push(`单日最大买入数${d.maxDailyBuy}只`)
      return parts.join('；')
    })

    const handleBacktest = async () => {
      if (!selectedFactors.value.length) {
        showToastMsg('第一步：请先选择选股因子')
        return
      }
      if (!selectedRanges.value.length) {
        showToastMsg('第二步：请先配置选股范围')
        return
      }
      if (!tradingData.value.valid) {
        showToastMsg('第三步：请完善买卖规则')
        return
      }
      if (positionData.value.valid === false) {
        showToastMsg('第四步：请完善仓位控制')
        return
      }
      if (backtestData.value.valid === false) {
        showToastMsg('第五步：请完善回测周期')
        return
      }

      backtesting.value = true
      try {
        const token = route.query.token as string
        const userInfo = store.state.userInfo
        const userName = userInfo?.userName || (userInfo as any)?.username

        const td = tradingData.value
        const pd = positionData.value
        const bd = backtestData.value

        const factor = selectedFactors.value.map(mapStarSelectionToApiItem)
        const stockRange = selectedRanges.value.map(mapStarSelectionToApiItem)

        // initialFund：万 → 元
        const initialFund = pd.initCapital ? Math.round(parseFloat(pd.initCapital) * 10000) : 1000000

        const body: Record<string, any> = {
          factor,
          buyTime: td.buyTime ?? 1,
          buyPriority: td.buyPriority || undefined,
          buyPriorityDirection: td.buyPriorityDirection || undefined,
          holdPeriod: td.holdPeriod ?? undefined,
          sellOptions: td.sellOptions?.length ? td.sellOptions : undefined,
          takeProfit: td.takeProfit ?? undefined,
          sellStopLoss: td.sellStopLoss ?? undefined,
          sellStockReturn: td.sellStockReturn ?? undefined,
          sellFallenBack: td.sellFallenBack ?? undefined,
          sellTime: td.sellTime ?? 1,
          initialFund,
          positionLimit: pd.maxSinglePos ? parseInt(pd.maxSinglePos) : undefined,
          maxHolds: pd.maxHoldCount ? parseInt(pd.maxHoldCount) : undefined,
          dayLimit: pd.maxDailyBuy ? parseInt(pd.maxDailyBuy) : undefined,
          period: bd.period || 'year1',
          periodStart: bd.periodStart ?? undefined,
          periodEnd: bd.periodEnd ?? undefined,
        }

        if (isPassword.value) {
          body.watchWord = route.query.watchWord as string || undefined
        }
        if (stockRange.length) {
          body.stockRange = stockRange
        }

        // 清理 undefined
        Object.keys(body).forEach(k => body[k] === undefined && delete body[k])

        const res = await starApi.doTestCom({
          data: body,
          headers: { token, appcode: 'com.rjhy.uranus', userName }
        })

        const taskID = res?.data?.taskID
        handleOneClickBacktestStatus(res?.data?.status, res?.data?.toast, () => {
          starPush('/star/group/result', taskID ? { taskID, isfrom: '1', isocbt: '1', ...(isPassword.value ? { isFactor: '1' } : {}) } : { isfrom: '1', isocbt: '1' })
        }, userName)
      } catch (e) {
        // 失败不跳转
      } finally {
        backtesting.value = false
      }
    }

    watch(
      () => resolveRouteWatchWord(),
      async (word, prev) => {
        if (!word || word === prev) return
        await applyWatchwordFromRoute()
      },
    )

    watch(
      () => [watchwordData.value, factorOptions.value, stockRangeOptions.value] as const,
      () => {
        if (hasWatchWord.value && watchwordData.value && resolveFactorTree(factorOptions.value).length) {
          void hydrateWatchwordSelections()
        }
      },
    )

    onMounted(async () => {
      if (isXpLowVersion(route.query as Record<string, unknown>)) return
      await Promise.allSettled([
        fetchComOptions(),
        fetchFactors(),
        hasWatchWord.value ? fetchWatchword() : Promise.resolve(),
      ])
      if (watchwordData.value) {
        await hydrateWatchwordSelections()
      }
      if (!watchwordData.value && !hasWatchWord.value) {
        await initStockRangeForNormalMode()
      }
    })

    return {
      activeTopTab: ref('home'),
      colors, isLight, isPassword,
      comOptions, factorOptions, factorOptionsForDisplay, stockRangeOptions, stockRangeOptionsForDisplay,
      factorHeaders, factorInitialSelection, rangeInitialSelection,
      selectedFactors, selectedRanges,
      rangeStockTotal, rangeStockList, rangeStockLoading, rangeDialogListLoading,
      formatStockCode, formatPrice, formatPct, pxRateColor,
      onOpenSelectedStockDialog,
      watchwordData, backtestPeriodInitial,
      strategyNameText, strategyNoteText, strategyBuiltinPlacements,
      factorPasswordNoAuthHint,
      tradingData, positionData, backtestData,
      sellSummary, positionSummary,
      showStockDialog, showFactorTip, backtesting, showToast, toastMsg,
      tipDialog, unlock,
      handleUnlockClose, handleUnlockBannerClick, handleUnlockShareClose, handleUnlockPromoClose,
      handleUnlockPurchaseClick,
      showActivationDialog, showActivationQr, openActivationQr, refreshActivation, hideActivation, route, starUserName,
      onFactorChange, onRangeChange, onTradingChange, onPositionChange, onBacktestChange,
      goHome, handleBacktest
    }
  }
})

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "9f880c32": (_ctx.isLight ? '#f0f2f5' : '#2a2a2a'),
  "1bfe962f": (_ctx.isLight ? '1px solid #dbdfe6' : 'none'),
  "6f6c546f": (_ctx.isLight ? '0 1px 4px rgba(0,0,0,0.06)' : 'none')
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__